import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';
import { SuiRadio, SuiRadioGroup, SuiTextInput, SuiSpinner } from '@splashthat/sui';

import FormUsageText from 'common/components/FormLibrary/FormUsage/FormUsageText';

import { getFormLibraryError, getFormLibraryStatus } from '../../selectors/formLibrary';
import { setFormLibraryError } from '../../actions/formLibrary';
import { LIST_THEMES_EVENTS } from './ScaledFormSaveModal';
import { SAVING, FETCHING } from '../../reducers/formLibrary';

export const ContentContainer = styled.div`
    padding: 0 3rem;
    display: flex;
    flex-direction: column;
    justify-content: center;
    height: 100%;
`;

const ControlsContainer = styled.div`
    display: flex;
    flex-direction: column;
    padding: 0 2rem;
`;

const TextInput = styled(SuiTextInput)`
    margin: 1rem 0;
`;

const SuiRadioGroupStyled = styled(SuiRadioGroup)`
    .SuiRadioGroup-option {
        padding-bottom: 0;
    }
`;

export const SAVE_RETROACTIVELY = 'saveRetroactively';
export const SAVE_AS_COPY = 'saveAsCopy';
export const FORM_NAME_ERROR_MESSAGE = 'This name is already in use. Please choose a different one.';
const UPDATE_EXISTING_LABEL = 'Save changes to form template';
const SAVE_AS_COPY_LABEL = 'Save changes on this event only';
export const FORM_TEMPLATE_NAME = 'Form Template Name';
export const NAME_PLACEHOLDER = 'Enter Form Name';

const COMPONENT_NAME = 'ScaledFormSaveContent';

const MAX_ALLOWED_CHAR = 100;

const StyledSpinner = styled.div`
    align-self: center;
`;

const ScaledFormsSaveContent = ({
    formName,
    themeCount,
    eventCount,
    onChangeTitle,
    setShownModalContent,
    setSaveMethod,
    saveMethod,
    savedFormName,
}) => {
    const dispatch = useDispatch();
    const error = useSelector(getFormLibraryError);
    const status = useSelector(getFormLibraryStatus);

    const loading = status === SAVING || status === FETCHING;

    const changeTitle = (event, value) => {
        if (value.length <= MAX_ALLOWED_CHAR) {
            onChangeTitle(value);
            error && dispatch(setFormLibraryError(false));
        }
    };

    if (loading) {
        return (
            <StyledSpinner>
                <SuiSpinner />
            </StyledSpinner>
        );
    }

    return (
        <ContentContainer>
            <FormUsageText
                themeCount={themeCount}
                eventCount={eventCount}
                onClickText={() => setShownModalContent(LIST_THEMES_EVENTS)}
                formName={savedFormName}
            />
            <ControlsContainer>
                <SuiRadioGroupStyled value={saveMethod} onChange={(ev, value) => setSaveMethod(value)}>
                    <SuiRadio
                        key={SAVE_RETROACTIVELY}
                        value={SAVE_RETROACTIVELY}
                        label={UPDATE_EXISTING_LABEL}
                        data-qa={`${COMPONENT_NAME}__option-${SAVE_RETROACTIVELY}`}
                    />
                    <SuiRadio
                        key={SAVE_AS_COPY}
                        value={SAVE_AS_COPY}
                        label={SAVE_AS_COPY_LABEL}
                        data-qa={`${COMPONENT_NAME}__option-${SAVE_AS_COPY}`}
                    />
                </SuiRadioGroupStyled>
                <TextInput
                    disabled={saveMethod === SAVE_AS_COPY}
                    label={FORM_TEMPLATE_NAME}
                    placeholder={NAME_PLACEHOLDER}
                    value={formName}
                    maxLength={MAX_ALLOWED_CHAR}
                    onChange={changeTitle}
                    data-qa={`${COMPONENT_NAME}__input-name`}
                    errorText={error && saveMethod === SAVE_RETROACTIVELY ? FORM_NAME_ERROR_MESSAGE : ''}
                    variation={error && saveMethod === SAVE_RETROACTIVELY ? 'error' : ''}
                />
            </ControlsContainer>
        </ContentContainer>
    );
};

ScaledFormsSaveContent.propTypes = {
    formName: PropTypes.string,
    themeCount: PropTypes.number.isRequired,
    eventCount: PropTypes.number.isRequired,
    onChangeTitle: PropTypes.func.isRequired,
    setShownModalContent: PropTypes.func.isRequired,
    setSaveMethod: PropTypes.func.isRequired,
    saveMethod: PropTypes.string.isRequired,
    savedFormName: PropTypes.string.isRequired,
};

ScaledFormsSaveContent.defaultProps = {
    formName: '',
};

export default ScaledFormsSaveContent;
